import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { mediaAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Image as ImageIcon, Upload, Trash2, Copy, Check, Link, Globe,
  FolderOpen, Plus, X, Search, Download, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMedia = () => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // 'upload' | 'url' | 'scrape'
  const [previewFile, setPreviewFile] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [previewImages, setPreviewImages] = useState([]);

  // URL import state
  const [importUrl, setImportUrl] = useState('');
  const [urlPreview, setUrlPreview] = useState(null);
  const [urlValid, setUrlValid] = useState(null);
  const [importingUrl, setImportingUrl] = useState(false);

  // Scrape state
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapedImages, setScrapedImages] = useState([]);
  const [selectedScrape, setSelectedScrape] = useState(new Set());
  const [importingScrape, setImportingScrape] = useState(false);

  useEffect(() => { loadData(); }, [activeFolder]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [filesData, foldersData] = await Promise.all([
        mediaAPI.list(activeFolder || null),
        mediaAPI.getFolders()
      ]);
      setFiles(filesData);
      setFolders(foldersData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // Convert /uploads/ to /api/uploads/ for proxy compatibility
    const path = url.startsWith('/uploads/') ? `/api${url}` : url;
    return `${BACKEND_URL}${path}`;
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(getFullUrl(url));
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ===== UPLOAD FROM COMPUTER =====
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const previews = selected.map(f => ({ file: f, name: f.name, preview: URL.createObjectURL(f), size: f.size }));
    setPreviewImages(previews);
  };

  const handleUploadConfirm = async () => {
    if (previewImages.length === 0) return;
    setUploading(true);
    const folder = newFolderName.trim() || uploadFolder;
    try {
      for (const item of previewImages) {
        await mediaAPI.upload(item.file, item.name, '', folder);
      }
      setPreviewImages([]);
      setNewFolderName('');
      setActiveTab(null);
      if (folder) setActiveFolder(folder);
      loadData();
    } catch (error) {
      alert('Erro no upload: ' + (error.response?.data?.detail || error.message));
    } finally { setUploading(false); }
  };

  const removePreview = (idx) => {
    setPreviewImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  // ===== IMPORT FROM URL =====
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch { return false; }
  };

  const handleUrlCheck = () => {
    if (!validateUrl(importUrl)) {
      setUrlValid(false);
      setUrlPreview(null);
      return;
    }
    setUrlValid(true);
    setUrlPreview(importUrl);
  };

  const handleUrlImport = async () => {
    if (!urlPreview) return;
    setImportingUrl(true);
    try {
      await mediaAPI.importUrl(importUrl, '', uploadFolder || newFolderName.trim());
      setImportUrl('');
      setUrlPreview(null);
      setUrlValid(null);
      setActiveTab(null);
      loadData();
    } catch (error) {
      alert('Erro ao importar: ' + (error.response?.data?.detail || error.message));
    } finally { setImportingUrl(false); }
  };

  // ===== SCRAPE FROM PAGE =====
  const handleScrape = async () => {
    if (!validateUrl(scrapeUrl)) { alert('URL inválida'); return; }
    setScraping(true);
    setScrapedImages([]);
    setSelectedScrape(new Set());
    try {
      const result = await mediaAPI.scrapePage(scrapeUrl);
      setScrapedImages(result.images || []);
    } catch (error) {
      alert('Erro ao buscar: ' + (error.response?.data?.detail || error.message));
    } finally { setScraping(false); }
  };

  const toggleScrapeSelect = (url) => {
    setSelectedScrape(prev => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const selectAllScrape = () => {
    if (selectedScrape.size === scrapedImages.length) setSelectedScrape(new Set());
    else setSelectedScrape(new Set(scrapedImages.map(i => i.url)));
  };

  const handleScrapeImport = async () => {
    if (selectedScrape.size === 0) return;
    setImportingScrape(true);
    try {
      const folder = newFolderName.trim() || uploadFolder;
      const result = await mediaAPI.importMultiple(Array.from(selectedScrape), folder);
      alert(`${result.imported} imagens importadas${result.errors?.length ? `, ${result.errors.length} erros` : ''}`);
      setScrapedImages([]);
      setSelectedScrape(new Set());
      setScrapeUrl('');
      setActiveTab(null);
      loadData();
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    } finally { setImportingScrape(false); }
  };

  // ===== DELETE =====
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir "${name}"?`)) return;
    try {
      await mediaAPI.delete(id);
      setFiles(files.filter(f => f.id !== id));
    } catch (error) { alert('Erro: ' + (error.response?.data?.detail || error.message)); }
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 data-testid="media-page-title" className="text-2xl font-bold text-gray-800">Biblioteca de Mídia</h1>
            <p className="text-gray-600">{files.length} arquivos{activeFolder ? ` em "${activeFolder}"` : ''}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="btn-upload-computer" onClick={() => setActiveTab(activeTab === 'upload' ? null : 'upload')} variant={activeTab === 'upload' ? 'default' : 'outline'} className={activeTab === 'upload' ? 'bg-[#055509] text-white' : ''}>
              <Upload size={16} className="mr-2" />Upload
            </Button>
            <Button data-testid="btn-import-url" onClick={() => setActiveTab(activeTab === 'url' ? null : 'url')} variant={activeTab === 'url' ? 'default' : 'outline'} className={activeTab === 'url' ? 'bg-[#055509] text-white' : ''}>
              <Link size={16} className="mr-2" />Importar URL
            </Button>
            <Button data-testid="btn-scrape-page" onClick={() => setActiveTab(activeTab === 'scrape' ? null : 'scrape')} variant={activeTab === 'scrape' ? 'default' : 'outline'} className={activeTab === 'scrape' ? 'bg-[#055509] text-white' : ''}>
              <Globe size={16} className="mr-2" />Buscar de Página
            </Button>
          </div>
        </div>

        {/* ===== UPLOAD FROM COMPUTER PANEL ===== */}
        {activeTab === 'upload' && (
          <Card className="mb-6 border-[#055509]">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2"><Upload size={20} className="text-[#055509]" />Upload do Computador</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setActiveTab(null); setPreviewImages([]); }}><X size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <select value={uploadFolder} onChange={e => setUploadFolder(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                  <option value="">Sem pasta</option>
                  {folders.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <Input placeholder="Ou criar nova pasta..." value={newFolderName} onChange={e => setNewFolderName(e.target.value)} className="max-w-[200px]" />
              </div>
              <label data-testid="file-input-upload" className="block">
                <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" onChange={handleFileSelect} className="hidden" />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#055509] transition-colors">
                  <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600 font-medium">Clique para selecionar arquivos</p>
                  <p className="text-sm text-gray-400">JPG, PNG, WEBP</p>
                </div>
              </label>
              {previewImages.length > 0 && (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {previewImages.map((item, i) => (
                      <div key={i} className="relative group border rounded-lg overflow-hidden">
                        <img src={item.preview} alt={item.name} className="w-full h-24 object-cover" />
                        <button onClick={() => removePreview(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"><X size={12} /></button>
                        <p className="text-[10px] px-1 truncate">{item.name}</p>
                      </div>
                    ))}
                  </div>
                  <Button data-testid="confirm-upload" onClick={handleUploadConfirm} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={uploading}>
                    {uploading ? 'Enviando...' : `Enviar ${previewImages.length} arquivo(s)`}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== IMPORT FROM URL PANEL ===== */}
        {activeTab === 'url' && (
          <Card className="mb-6 border-[#055509]">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2"><Link size={20} className="text-[#055509]" />Importar por URL</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setActiveTab(null); setUrlPreview(null); setImportUrl(''); }}><X size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <select value={uploadFolder} onChange={e => setUploadFolder(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                  <option value="">Sem pasta</option>
                  {folders.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <Input placeholder="Ou criar nova pasta..." value={newFolderName} onChange={e => setNewFolderName(e.target.value)} className="max-w-[200px]" />
              </div>
              <div className="flex gap-2">
                <Input data-testid="url-input" placeholder="Cole a URL direta da imagem..." value={importUrl} onChange={e => { setImportUrl(e.target.value); setUrlValid(null); setUrlPreview(null); }} className="flex-1" onKeyDown={e => e.key === 'Enter' && handleUrlCheck()} />
                <Button onClick={handleUrlCheck} variant="outline"><Eye size={16} className="mr-1" />Validar</Button>
              </div>
              {urlValid === false && <p className="text-red-500 text-sm">URL inválida. Verifique o endereço.</p>}
              {urlPreview && (
                <div className="space-y-3">
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700 mb-2">Prévia:</p>
                    <img src={urlPreview} alt="Preview" className="max-h-48 rounded object-contain" onError={() => { setUrlValid(false); setUrlPreview(null); }} />
                  </div>
                  <Button data-testid="confirm-url-import" onClick={handleUrlImport} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={importingUrl}>
                    {importingUrl ? 'Importando...' : 'Importar Imagem'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== SCRAPE FROM PAGE PANEL ===== */}
        {activeTab === 'scrape' && (
          <Card className="mb-6 border-[#055509]">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2"><Globe size={20} className="text-[#055509]" />Buscar Imagens de Página</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setActiveTab(null); setScrapedImages([]); setScrapeUrl(''); }}><X size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">Cole o endereço de qualquer página e o sistema buscará as imagens disponíveis.</p>
              <div className="flex gap-3">
                <select value={uploadFolder} onChange={e => setUploadFolder(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                  <option value="">Sem pasta</option>
                  {folders.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <Input placeholder="Ou criar nova pasta..." value={newFolderName} onChange={e => setNewFolderName(e.target.value)} className="max-w-[200px]" />
              </div>
              <div className="flex gap-2">
                <Input data-testid="scrape-url-input" placeholder="https://exemplo.com/pagina-do-produto" value={scrapeUrl} onChange={e => setScrapeUrl(e.target.value)} className="flex-1" onKeyDown={e => e.key === 'Enter' && handleScrape()} />
                <Button data-testid="scrape-btn" onClick={handleScrape} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={scraping}>
                  <Search size={16} className="mr-1" />{scraping ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
              {scrapedImages.length > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-700">{scrapedImages.length} imagens encontradas</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllScrape}>
                        {selectedScrape.size === scrapedImages.length ? 'Desmarcar todas' : 'Selecionar todas'}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 max-h-[400px] overflow-y-auto">
                    {scrapedImages.map((img, i) => (
                      <div key={i} onClick={() => toggleScrapeSelect(img.url)} className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${selectedScrape.has(img.url) ? 'border-[#055509] ring-2 ring-[#055509] ring-opacity-30' : 'border-gray-200 hover:border-gray-400'}`}>
                        <img src={img.url} alt={img.alt || ''} className="w-full h-24 object-cover bg-gray-100" onError={e => e.target.style.display = 'none'} />
                        {selectedScrape.has(img.url) && (
                          <div className="absolute top-1 right-1 bg-[#055509] text-white rounded-full p-0.5"><Check size={12} /></div>
                        )}
                        <p className="text-[9px] px-1 truncate text-gray-500">{img.alt || new URL(img.url).pathname.split('/').pop()}</p>
                      </div>
                    ))}
                  </div>
                  {selectedScrape.size > 0 && (
                    <Button data-testid="confirm-scrape-import" onClick={handleScrapeImport} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={importingScrape}>
                      {importingScrape ? 'Importando...' : `Importar ${selectedScrape.size} imagem(ns)`}
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== FOLDERS ===== */}
        {folders.length > 0 && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button data-testid="folder-all" variant={!activeFolder ? 'default' : 'outline'} size="sm" onClick={() => setActiveFolder('')} className={!activeFolder ? 'bg-[#055509] text-white' : ''}>
              <FolderOpen size={14} className="mr-1" />Todos
            </Button>
            {folders.map(f => (
              <Button key={f} data-testid={`folder-${f}`} variant={activeFolder === f ? 'default' : 'outline'} size="sm" onClick={() => setActiveFolder(f)} className={activeFolder === f ? 'bg-[#055509] text-white' : ''}>
                <FolderOpen size={14} className="mr-1" />{f}
              </Button>
            ))}
          </div>
        )}

        {/* ===== FILE GRID ===== */}
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
            ) : files.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                <p className="font-medium">Nenhum arquivo na biblioteca</p>
                <p className="text-sm mt-2">Use os botões acima para adicionar imagens</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {files.map(file => (
                  <div key={file.id} className="group relative border rounded-lg overflow-hidden bg-white" data-testid={`media-file-${file.id}`}>
                    <div className="cursor-pointer" onClick={() => setPreviewFile(file)}>
                      <img src={getFullUrl(file.url)} alt={file.alt || file.originalName} className="w-full h-32 object-cover" onError={e => { e.target.src = ''; e.target.className = 'w-full h-32 bg-gray-200 flex items-center justify-center'; }} />
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-gray-800 truncate font-medium">{file.originalName}</p>
                      <div className="flex justify-between text-[10px] text-gray-500">
                        <span>{formatSize(file.size)}</span>
                        {file.folder && <span className="bg-gray-100 px-1 rounded">{file.folder}</span>}
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button data-testid={`copy-url-${file.id}`} onClick={() => copyUrl(file.url)} className="p-2 bg-white rounded-full shadow" title="Copiar URL">
                        {copiedId === file.url ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      </button>
                      <button data-testid={`delete-media-${file.id}`} onClick={() => handleDelete(file.id, file.originalName)} className="p-2 bg-red-500 text-white rounded-full shadow" title="Excluir">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ===== PREVIEW MODAL ===== */}
        {previewFile && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewFile(null)}>
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="font-bold text-gray-800">{previewFile.originalName}</h3>
                <Button variant="ghost" size="sm" onClick={() => setPreviewFile(null)}><X size={18} /></Button>
              </div>
              <div className="p-4">
                <img src={getFullUrl(previewFile.url)} alt={previewFile.alt || previewFile.originalName} className="w-full max-h-[60vh] object-contain rounded" />
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Tamanho:</span> <span className="font-medium">{formatSize(previewFile.size)}</span></div>
                  <div><span className="text-gray-500">Tipo:</span> <span className="font-medium">{previewFile.mimeType}</span></div>
                  {previewFile.folder && <div><span className="text-gray-500">Pasta:</span> <span className="font-medium">{previewFile.folder}</span></div>}
                  {previewFile.sourceUrl && <div className="col-span-2"><span className="text-gray-500">Fonte:</span> <a href={previewFile.sourceUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline truncate block">{previewFile.sourceUrl}</a></div>}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyUrl(previewFile.url)}>
                    <Copy size={14} className="mr-1" />Copiar URL
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => { handleDelete(previewFile.id, previewFile.originalName); setPreviewFile(null); }}>
                    <Trash2 size={14} className="mr-1" />Excluir
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
